import React from 'react';
import { Box, Typography, TextField, Button, InputAdornment, CircularProgress } from '@mui/material';
import {
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const ProductImages = ({
  aiCapturedImage,
  webImages,
  selectedWebImages,
  isSearchingImages,
  currentSearchQuery,
  customSearchQuery,
  setCustomSearchQuery,
  onSearch,
  onToggle,
  onDelete,
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        🖼️ Product Images
        <Typography variant="caption" color="text.secondary">
          (Select images to include)
        </Typography>
      </Typography>

      {currentSearchQuery && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Image Search Query:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={customSearchQuery}
              onChange={(e) => setCustomSearchQuery(e.target.value)}
              placeholder="Edit search query and press search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  onSearch();
                }
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={onSearch}
              disabled={isSearchingImages || !customSearchQuery.trim()}
              sx={{ minWidth: '80px' }}
            >
              {isSearchingImages ? <CircularProgress size={16} /> : 'Search'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Current query: "{currentSearchQuery}" • {webImages.length} images found
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mb: 2, pb: 1 }}>
        <Box
          sx={{
            position: 'relative',
            borderRadius: 1,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: 'primary.main',
            minWidth: 100,
            width: 100,
            height: 100,
            flexShrink: 0,
          }}
        >
          {aiCapturedImage?.url && (
            <img
              src={aiCapturedImage.url}
              alt="User uploaded"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              left: 2,
              bgcolor: 'primary.main',
              color: 'white',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.6rem',
            }}
          >
            Original
          </Box>
        </Box>

        {webImages &&
          webImages.map((image, index) => {
            const url = typeof image === 'string' ? image : image.url;
            const isSelected = selectedWebImages.includes(url);
            return (
              <Box
                key={url || index}
                onClick={() => onToggle(url)}
                sx={{
                  position: 'relative',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  minWidth: 100,
                  width: 100,
                  height: 100,
                  flexShrink: 0,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.light',
                    transform: 'scale(1.02)',
                  },
                }}
              >
                <img
                  src={url}
                  alt={`Product view ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />

                <Box
                  onClick={(e) => onDelete(url, e)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'error.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    opacity: 0.8,
                    '&:hover': { opacity: 1, transform: 'scale(1.1)' },
                    transition: 'all 0.2s',
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 12 }} />
                </Box>

                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      bgcolor: 'rgba(0,0,0,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                )}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    bgcolor: 'success.main',
                    color: 'white',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.6rem',
                  }}
                >
                  Web
                </Box>
              </Box>
            );
          })}

        {isSearchingImages && (
          <>
            {[1, 2, 3, 4].map((n) => (
              <Box
                key={n}
                sx={{
                  minWidth: 100,
                  width: 100,
                  height: 100,
                  flexShrink: 0,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed',
                  borderColor: 'grey.300',
                }}
              >
                <CircularProgress size={20} />
              </Box>
            ))}
          </>
        )}

        {!isSearchingImages && webImages.length === 0 && currentSearchQuery && (
          <Box
            sx={{
              minWidth: 200,
              height: 100,
              bgcolor: 'grey.50',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed',
              borderColor: 'grey.300',
              flexDirection: 'column',
            }}
          >
            <ImageIcon sx={{ color: 'grey.400', fontSize: 30, mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              No images found
            </Typography>
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        💡 Web images are fetched based on product details. Use the search bar above to find more images or refine your search.
      </Typography>
    </Box>
  );
};

export default ProductImages;
